import {DEFAULT_NOTIFICATIONS_LIMIT, IEmployeeNotification} from "@source/api/models/notificationModels";
import {makeAutoObservable} from "mobx";

export type NotificationTab = "new" | "all";

class NotificationsStore {
  notifications: IEmployeeNotification[] = [];
  newNotifications: IEmployeeNotification[] = [];
  newIds: string[] = [];
  tab: NotificationTab = "all";
  newLoaded = false;
  allLoaded = false;
  unreadCount = 0;
  hasMore = true;
  listOpen = false;
  unreadPromptOpen = false;
  unreadPromptDismissed = false;

  constructor() {
    makeAutoObservable(this);
  }

  setUnreadCount(count: number) {
    this.unreadCount = count;
  }

  showUnreadPrompt() {
    if (this.unreadCount > 0 && !this.unreadPromptDismissed && !this.listOpen) {
      this.unreadPromptOpen = true;
    }
  }

  showsNewDot(notification: IEmployeeNotification) {
    return notification.readAt == null || this.newIds.indexOf(notification._id) !== -1;
  }

  setTab(tab: NotificationTab) {
    this.tab = tab;
  }

  setNewNotifications(notifications: IEmployeeNotification[]) {
    const incomingIds = new Set(notifications.map((notification) => notification._id));
    const arrivedWhileLoading = this.newNotifications.filter((notification) => !incomingIds.has(notification._id));
    this.newNotifications = [...arrivedWhileLoading, ...notifications];

    const ids = this.newIds.slice();
    this.newNotifications.forEach((notification) => {
      if (ids.indexOf(notification._id) === -1) ids.push(notification._id);
    });
    this.newIds = ids;
    this.newLoaded = true;
    this.tab = this.newNotifications.length ? "new" : "all";
  }

  setNotifications(notifications: IEmployeeNotification[]) {
    const incomingIds = new Set(notifications.map((notification) => notification._id));
    const arrivedWhileLoading = this.notifications.filter((notification) => !incomingIds.has(notification._id));
    this.notifications = [...arrivedWhileLoading, ...notifications];
    this.hasMore = notifications.length === DEFAULT_NOTIFICATIONS_LIMIT;
    this.allLoaded = true;
  }

  addNotifications(notifications: IEmployeeNotification[]) {
    const existingIds = new Set(this.notifications.map((notification) => notification._id));
    const nextNotifications = notifications.filter((notification) => !existingIds.has(notification._id));
    this.notifications = [...this.notifications, ...nextNotifications];
    this.hasMore = notifications.length === DEFAULT_NOTIFICATIONS_LIMIT;
  }

  receiveLiveNotification(notification?: IEmployeeNotification, count?: number) {
    if (typeof count === "number") {
      this.unreadCount = count;
    }

    if (!this.listOpen || !notification?._id) {
      return;
    }

    if (this.newIds.indexOf(notification._id) === -1) {
      this.newIds = [notification._id, ...this.newIds];
    }

    if (!this.newNotifications.some((item) => item._id === notification._id)) {
      this.newNotifications = [notification, ...this.newNotifications];
    }

    if (!this.notifications.some((item) => item._id === notification._id)) {
      this.notifications = [notification, ...this.notifications];
    }
  }

  openList() {
    this.unreadPromptOpen = false;
    this.unreadPromptDismissed = true;
    this.listOpen = true;
  }

  closeList() {
    this.listOpen = false;
    this.notifications = [];
    this.newNotifications = [];
    this.newIds = [];
    this.tab = "all";
    this.newLoaded = false;
    this.allLoaded = false;
    this.hasMore = true;
  }

  dismissUnreadPrompt() {
    this.unreadPromptOpen = false;
    this.unreadPromptDismissed = true;
  }

  resetSession() {
    this.closeList();
    this.unreadCount = 0;
    this.unreadPromptOpen = false;
    this.unreadPromptDismissed = false;
  }
}

export default new NotificationsStore();
